import { scheduler } from './utilities.js';

class TabsContainer extends HTMLElement {
  connectedCallback() {
    this.tabTitles = Array.from(this.querySelectorAll('.tab-panel__title'));
    this.panels = Array.from(this.querySelectorAll('.tab-panel'));

    this.style.setProperty('--tab-count', this.panels.length);

    this.#setInitialActiveTab();
    this.#addEventListeners();
  }

  #setInitialActiveTab() {
    // Check for server-rendered active panel
    const activePanel = this.panels.find((p) => p.hasAttribute('active'));

    if (activePanel) {
      const tabId = activePanel.id.replace('tab-panel-', '');

      if (this.#isValidTabId(tabId)) {
        this.#activateTab(tabId);
        return;
      }
    }

    // Fallback: activate first tab
    if (this.tabTitles[0]) {
      const firstId = this.tabTitles[0]
        .getAttribute('aria-controls')
        .replace('tab-panel-', '');

      this.#activateTab(firstId);
    }
  }

  #isValidTabId(tabId) {
    return this.panels.some((panel) => panel.id === `tab-panel-${tabId}`);
  }

  #addEventListeners() {
    this.tabTitles.forEach((title, index) => {
      title.style.setProperty('--tab-index', index);

      title.addEventListener('click', () => {
        const tabId = title
          .getAttribute('aria-controls')
          .replace('tab-panel-', '');

        this.#activateTab(tabId);
      });

      title.addEventListener('keydown', (e) => {
        const titles = this.tabTitles;
        const currentIndex = titles.indexOf(title);
        let nextIndex = -1;

        switch (e.key) {
          case 'ArrowRight':
            nextIndex = (currentIndex + 1) % titles.length;
            break;
          case 'ArrowLeft':
            nextIndex = (currentIndex - 1 + titles.length) % titles.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = titles.length - 1;
            break;
          default:
            return;
        }

        e.preventDefault();

        const nextTitle = titles[nextIndex];
        nextTitle.focus();

        const tabId = nextTitle
          .getAttribute('aria-controls')
          .replace('tab-panel-', '');

        this.#activateTab(tabId);
      });
    });
  }

  #activateTab(tabId) {
    const panelId = `tab-panel-${tabId}`;

    this.tabTitles.forEach((title) => {
      const isSelected = title.getAttribute('aria-controls') === panelId;

      title.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      title.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      if (panel.id === panelId) {
        panel.setAttribute('active', '');
      } else {
        panel.removeAttribute('active');
      }
    });

    scheduler.schedule(() => {
      // Optional: track tab view here if needed.
      // Theme.event?.dispatch('tab_view', { tab_id: tabId });
    });
  }
}

if (!customElements.get('tabs-container')) {
  customElements.define('tabs-container', TabsContainer);
}